// feature-flag.service.ts
import { Injectable } from '@angular/core';
import flagsmith, { ClientEvaluationContext } from 'flagsmith';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private readonly baseRetryDelayMs = 1000;
  private readonly maxRetryDelayMs = 30000;

  private get canUseFlags(): boolean {
    return this.initialized;
  }

  private scheduleReconnect(reason: string): void {
    if (this.canUseFlags || this.initPromise || this.reconnectTimer) return;

    const delay = Math.min(this.baseRetryDelayMs * 2 ** this.retryAttempt, this.maxRetryDelayMs);
    this.retryAttempt += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.init();
    }, delay);

    console.warn(`FeatureFlagService: reconnect scheduled in ${delay}ms (${reason}).`);
  }

  private markDisconnected(operation: string, error: unknown): void {
    this.initialized = false;
    console.warn(`FeatureFlagService: ${operation} failed, marking as disconnected.`, error);
    this.scheduleReconnect(`${operation} failed`);
  }

  async init(): Promise<void> {
    if (this.canUseFlags) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        await flagsmith.init({
          api: environment.flagsmithAPI,
          environmentID: environment.flagsmithEnvironmentID,
          cacheFlags: true,
          enableAnalytics: false,
        });

        this.initialized = true;
        this.retryAttempt = 0;

        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      } catch (error) {
        this.initialized = false;
        console.warn('FeatureFlagService: failed to initialize, all flags will default to disabled.');
        this.scheduleReconnect('init failed');
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  isEnabled(flag: string): boolean {
    if (!this.canUseFlags) {
      this.scheduleReconnect('isEnabled called while not initialized');
      return false;
    }

    try {
      return flagsmith.hasFeature(flag);
    } catch (error) {
      this.markDisconnected('hasFeature', error);
      return false;
    }
  }

  isActive(flag: string): boolean {
    if (!this.canUseFlags) {
      this.scheduleReconnect('isActive called while not initialized');
      return false;
    }

    try {
      // Only return true if flag value is strictly true
      return flagsmith.getValue(flag) === true;
    } catch (error) {
      this.markDisconnected('getValue', error);
      return false;
    }
  }

  /**
   * @param identity: string - user identity
   * @param traits: object - property traits
   * @returns Promise<any> - object contains flags
   */
  async getIdentityFlagEnabled(
    identity: string,
    traits: Record<string, string | number | boolean | null>,
    flag: string,
  ): Promise<boolean> {
    if (!this.canUseFlags) {
      await this.init();
      if (!this.canUseFlags) return false;
    }

    try {
      await flagsmith.identify(identity, traits);
      return flagsmith.hasFeature(flag);
    } catch (error) {
      this.markDisconnected('identify', error);
      return false;
    }
  }

  async getIdentityFlagValue(
    identity: string,
    traits: Record<string, string | number | boolean | null>,
    flag: string,
  ): Promise<any> {
    if (!this.canUseFlags) {
      await this.init();
      if (!this.canUseFlags) return null;
    }

    try {
      await flagsmith.identify(identity, traits);
      const hasFeature = flagsmith.hasFeature(flag);
      const val = flagsmith.getValue(flag);
      if (val !== null && val !== undefined) {
        return val;
      }
      return hasFeature ? true : null;
    } catch (error) {
      this.markDisconnected('identify', error);
      return null;
    }
  }

  /**
   * Clear the current identity and reset flags.
   */
  async clearIdentity(): Promise<void> {
    if (!this.canUseFlags) {
      this.scheduleReconnect('clearIdentity called while not initialized');
      return;
    }

    try {
      await flagsmith.logout();
    } catch (error) {
      this.markDisconnected('logout', error);
    }
  }

  /**
   * Set evaluation context. Refresh the flags.
   * @param context: ClientEvaluationContext
   */
  async setContext(context: ClientEvaluationContext): Promise<void> {
    if (!this.canUseFlags) {
      await this.init();
      if (!this.canUseFlags) return;
    }

    try {
      await flagsmith.setContext(context);
    } catch (error) {
      this.markDisconnected('setContext', error);
    }
  }
}
